import { forwardRef } from 'react'
import type { ChangeEvent } from 'react'
import { RequiredMark, useFieldIds } from '../ui/fieldChrome'

export interface FormFieldProps {
  readonly id: string
  readonly label: string
  readonly type?: 'text' | 'email' | 'password'
  readonly autoComplete?: string
  readonly value: string
  readonly onChange: (value: string) => void
  /** Field-level validation/server message. Rendered inline and wired via
   * `aria-describedby` + `aria-invalid` so assistive tech announces it when
   * the field receives focus — the accessibility-wcag skill's
   * label/describedby/aria-invalid contract for form errors. */
  readonly error?: string | undefined
  readonly hint?: string | undefined
  readonly required?: boolean
  readonly minLength?: number
  readonly maxLength?: number
}

/**
 * One labeled input, used by every F0.3 auth form (login/signup/verification).
 * Deliberately semantic-HTML-first (`<label htmlFor>`, native `<input>`) per
 * the accessibility-wcag skill — no ARIA is needed to fill a gap here, only
 * `aria-describedby`/`aria-invalid` to associate the error text.
 *
 * Forwards a ref to the underlying `<input>` so callers can move focus to it
 * — the same focus-management contract `FormAlert` uses for server errors
 * (USP-42: client-side validation errors must move focus/announce just like
 * the server-error and step-change paths already do).
 */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  {
    id,
    label,
    type = 'text',
    autoComplete,
    value,
    onChange,
    error,
    hint,
    required,
    minLength,
    maxLength,
  },
  ref,
) {
  // Shares the field id wiring with the ui field primitives (fieldChrome) so
  // the ids can't drift (UID-229 review). FormField keeps its own layout (hint
  // ABOVE the input) and `form-field-*` classes, and — unlike the primitives —
  // renders hint AND error together, so it builds its own describedBy
  // referencing BOTH (the hook's `describedBy` is the error-XOR-help form the
  // primitives need, which would drop the hint idref here).
  const { errorId, helpId: hintId } = useFieldIds(id, { error, helpText: hint })
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined

  return (
    <div className="form-field">
      <label htmlFor={id} className="form-field-label">
        {label}
        {required && <RequiredMark />}
      </label>
      {hint && (
        <p id={hintId} className="form-field-hint caption-text">
          {hint}
        </p>
      )}
      <input
        ref={ref}
        id={id}
        name={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className="form-field-input"
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      />
      {error && (
        <p id={errorId} className="form-field-error">
          {error}
        </p>
      )}
    </div>
  )
})
